import type { SupabaseClient } from '@supabase/supabase-js';
import type {
  CatalogDimensions,
  CatalogPrimaryImage,
  CatalogProductMetadata,
  ProductMuscleGroup,
} from '@garagegymbros/contracts';

export type DimensionUnit = 'in' | 'cm';
export const emptyDimensions: CatalogDimensions = {
  width_mm: null,
  depth_mm: null,
  height_mm: null,
};

/** Approved catalog assets are served through the same-origin public catalog path. */
export function safeCatalogImageUrl(input: unknown): string | null {
  return typeof input === 'string' &&
    /^\/catalog\/(?:[a-zA-Z0-9_-]+\/)*[a-zA-Z0-9_-]+\.(?:webp|png|jpe?g)$/.test(input)
    ? input
    : null;
}
/** Never send card views to a remote source URL or an unreviewed photograph. */
export function catalogImageSources(image: CatalogPrimaryImage | null | undefined, large = false) {
  const full = safeCatalogImageUrl(image?.url);
  if (image?.photo_origin !== 'manufacturer' || !full) return [];
  const thumbnail = safeCatalogImageUrl(image.thumbnail_url);
  return !large && thumbnail && thumbnail !== full ? [thumbnail, full] : [full];
}
export function catalogLicenseLink(attribution: string | null | undefined) {
  const licenses = {
    'CC BY 2.0': 'https://creativecommons.org/licenses/by/2.0/',
    'CC BY-SA 2.0': 'https://creativecommons.org/licenses/by-sa/2.0/',
    'CC BY-SA 3.0': 'https://creativecommons.org/licenses/by-sa/3.0/',
    'CC BY-SA 4.0': 'https://creativecommons.org/licenses/by-sa/4.0/',
    'CC BY 4.0': 'https://creativecommons.org/licenses/by/4.0/',
  } as const;
  const matches = attribution?.match(/\bCC BY(?:-SA)? (?:2\.0|3\.0|4\.0)\b/g) ?? [];
  for (const label of matches)
    if (label in licenses) return { label, url: licenses[label as keyof typeof licenses] };
  return null;
}
export function safeCatalogSource(input: unknown): string | null {
  if (typeof input !== 'string') return null;
  try {
    const url = new URL(input);
    return url.protocol === 'https:' && !url.username && !url.password ? url.href : null;
  } catch {
    return null;
  }
}
function positive(input: unknown): number | null {
  const value =
    typeof input === 'number'
      ? input
      : typeof input === 'string' && input.trim()
        ? Number(input)
        : NaN;
  return Number.isFinite(value) && value > 0 ? value : null;
}
export function catalogDimensions(input: unknown): CatalogDimensions {
  const value = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  return {
    width_mm: positive(value.width_mm),
    depth_mm: positive(value.depth_mm),
    height_mm: positive(value.height_mm),
  };
}
export function readCatalogMetadata(input: unknown): Omit<CatalogProductMetadata, 'product_id'> {
  const value = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
  const muscles: ProductMuscleGroup[] = [];
  for (const item of Array.isArray(value.muscle_groups) ? value.muscle_groups : []) {
    if (!item || typeof item !== 'object') continue;
    const row = item as Record<string, unknown>;
    if (
      typeof row.slug === 'string' &&
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(row.slug) &&
      typeof row.name === 'string' &&
      row.name.trim() &&
      ['primary', 'secondary'].includes(String(row.role)) &&
      !muscles.some((muscle) => muscle.slug === row.slug)
    )
      muscles.push({
        slug: row.slug,
        name: row.name.slice(0, 100),
        role: row.role as ProductMuscleGroup['role'],
      });
  }
  let image: CatalogPrimaryImage | null = null;
  if (value.primary_image && typeof value.primary_image === 'object') {
    const row = value.primary_image as Record<string, unknown>,
      url = safeCatalogImageUrl(row.url);
    if (url && row.photo_origin === 'manufacturer')
      image = {
        url,
        thumbnail_url: safeCatalogImageUrl(row.thumbnail_url),
        photo_origin: 'manufacturer',
        alt: typeof row.alt === 'string' ? row.alt.slice(0, 300) : '',
        source_url: safeCatalogSource(row.source_url),
        attribution: typeof row.attribution === 'string' ? row.attribution.slice(0, 500) : null,
      };
  }
  return {
    dimensions: catalogDimensions(value.dimensions ?? value),
    muscle_groups: muscles,
    primary_image: image,
  };
}
export async function loadCatalogMetadata(
  client: SupabaseClient,
  ids: string[],
  signal?: AbortSignal,
) {
  const unique = [...new Set(ids.filter(Boolean))],
    rows = new Map<string, ReturnType<typeof readCatalogMetadata>>();
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += 50) chunks.push(unique.slice(i, i + 50));
  await Promise.all(
    chunks.map(async (productIds) => {
      const request = client.rpc('catalog_product_metadata', { product_ids: productIds });
      const result = await (signal ? request.abortSignal(signal) : request);
      if (result.error) throw new Error(result.error.message);
      for (const row of result.data ?? [])
        if (row && typeof row.product_id === 'string' && productIds.includes(row.product_id))
          rows.set(row.product_id, readCatalogMetadata(row));
    }),
  );
  return rows;
}
export function dimensionLimit(
  value: string | undefined,
  unit: DimensionUnit = 'in',
): number | null {
  if (!value?.trim()) return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0)
    throw new Error('Dimension limits must be positive numbers or left blank.');
  const millimetres = Number((numeric * (unit === 'cm' ? 10 : 25.4)).toFixed(4));
  if (!Number.isFinite(millimetres) || millimetres <= 0)
    throw new Error('Dimension limits must be positive numbers within the supported precision.');
  return millimetres;
}
/** Editing another field or changing display units must preserve source measurements exactly. */
export function editorDimensions(
  original: CatalogDimensions,
  values: { width: string; depth: string; height: string },
  unit: DimensionUnit,
  changed: { width: boolean; depth: boolean; height: boolean },
): CatalogDimensions {
  return {
    width_mm: changed.width ? dimensionLimit(values.width, unit) : original.width_mm,
    depth_mm: changed.depth ? dimensionLimit(values.depth, unit) : original.depth_mm,
    height_mm: changed.height ? dimensionLimit(values.height, unit) : original.height_mm,
  };
}
export function formatDimension(
  value: number | null | undefined,
  unit: DimensionUnit = 'in',
): string {
  return value && Number.isFinite(value) && value > 0
    ? `${(value / (unit === 'cm' ? 10 : 25.4)).toLocaleString('en-US', { maximumFractionDigits: 2 })} ${unit}`
    : 'Unknown';
}
